import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, BadgeCheck, CalendarClock, CreditCard, ShieldCheck, Stethoscope } from "lucide-react";
import { SlotPicker } from "../components/SlotPicker";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { consultApi, consultError, SELECTION_KEY } from "../lib/consult";
import { formatINR } from "../lib/constants";
import { useFreeConsult } from "../lib/useFreeConsult";
import { startConsultPayment } from "../lib/cashfree";
import { ButtonRing, PageLoader } from "../components/LoadingRing";

const STEPS = ["Selection", "Appointment", "Your Details", "Payment"];

const Input = ({ className = "", ...rest }) => (
    <input className={`w-full bg-bg0 border border-white/12 text-white px-3 py-3 text-sm outline-none focus:border-gold transition-colors placeholder:text-zinc-600 ${className}`} {...rest} />
);
const Label = ({ children }) => <span className="mono text-[10px] tracking-[0.18em] text-zinc-400">{children}</span>;

export default function ConsultationBooking() {
    const nav = useNavigate();
    const { customer, refresh } = useCustomerAuth();
    const [step, setStep] = useState(1);
    const [sel, setSel] = useState({ package_key: "", doctor_id: "", area_key: "" });
    const [slot, setSlot] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: "", email: "", phone: "", age: "", gender: "", city: "",
        for_whom: "self", member_name: "", notes: "",
    });
    const { eligible: freeConsult } = useFreeConsult(
        !customer && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email) ? form.email.trim().toLowerCase() : "");

    const { data, isLoading } = useQuery({
        queryKey: ["consult-config"],
        queryFn: async () => (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consult/config`)).json(),
    });
    const packages = data?.packages || [];
    const doctors = data?.doctors || [];
    const areas = data?.areas || [];

    useEffect(() => {
        let saved = {};
        try { saved = JSON.parse(sessionStorage.getItem(SELECTION_KEY) || "{}"); } catch { saved = {}; }
        setSel((s) => ({ ...s, ...saved }));
    }, []);

    useEffect(() => {
        if (!packages.length) return;
        setSel((s) => ({
            ...s,
            package_key: s.package_key || packages.find((p) => p.featured)?.key || packages[0].key,
            doctor_id: s.doctor_id || doctors[0]?.id || "",
        }));
    }, [packages, doctors]);

    useEffect(() => {
        if (!customer) return;
        setForm((f) => ({
            ...f, name: f.name || customer.name || "", email: f.email || customer.email || "",
            phone: f.phone || customer.phone || "",
        }));
    }, [customer]);

    const pkg = useMemo(() => packages.find((p) => p.key === sel.package_key), [packages, sel.package_key]);
    const doctor = useMemo(() => doctors.find((d) => d.id === sel.doctor_id), [doctors, sel.doctor_id]);
    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const next = () => {
        if (step === 2 && !slot) return toast.error("Please choose an appointment slot");
        if (step === 3) {
            if (!form.name.trim()) return toast.error("Please enter the patient name");
            if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return toast.error("Please enter a valid email");
            if (form.phone.replace(/\D/g, "").length < 10) return toast.error("Please enter a valid 10-digit phone number");
        }
        setStep((s) => Math.min(4, s + 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const confirm = async () => {
        setSaving(true);
        try {
            const { data: res } = await consultApi.post("/bookings", {
                package_key: sel.package_key, doctor_id: sel.doctor_id, area_key: sel.area_key || "",
                slot_date: slot.slot_date, slot_time: slot.slot_time, ...form,
            });
            if (res.token) localStorage.setItem("rhm_customer_token", res.token);
            sessionStorage.removeItem(SELECTION_KEY);
            await refresh();
            const booking = res.booking;
            if (booking.payment_status === "free" || Number(booking.amount || 0) <= 0) {
                toast.success(`Appointment confirmed — ${booking.consultation_id}`);
                nav(`/consultation/intake/${booking.id}`, { replace: true });
                return;
            }
            sessionStorage.setItem("rhm_pending_consult", booking.id);
            toast.message("Opening secure payment…");
            await startConsultPayment(booking.id);
        } catch (e) {
            toast.error(consultError(e));
        } finally {
            setSaving(false);
        }
    };

    if (isLoading) return <PageLoader />;

    return (
        <div className="pt-28 md:pt-32 pb-24" data-testid="consultation-booking-page">
            <div className="mx-auto max-w-3xl px-6 md:px-10">
                <div className="eyebrow mb-3">Book a consultation</div>
                <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight">
                    {STEPS[step - 1]}
                </h1>

                {/* progress */}
                <div className="mt-7 flex items-center gap-2" data-testid="booking-progress">
                    {STEPS.map((s, i) => (
                        <div key={s} className="flex-1">
                            <div className={`h-[3px] ${i < step ? "bg-gold" : "bg-white/12"}`} />
                            <div className={`mono text-[9px] tracking-[0.16em] mt-2 ${i < step ? "text-gold" : "text-zinc-600"}`}>
                                {String(i + 1).padStart(2, "0")} {s.toUpperCase()}
                            </div>
                        </div>
                    ))}
                </div>

                {/* STEP 1 */}
                {step === 1 && (
                    <div className="mt-10 space-y-10" data-testid="booking-step-1">
                        <div>
                            <div className="mono text-[10px] tracking-[0.22em] text-zinc-500">CONSULTATION PLAN</div>
                            <div className="mt-3 grid sm:grid-cols-2 gap-3">
                                {packages.map((p) => (
                                    <button key={p.key} onClick={() => setSel((s) => ({ ...s, package_key: p.key }))}
                                        data-testid={`booking-package-${p.key}`}
                                        className={`text-left p-5 border transition-colors ${
                                            sel.package_key === p.key ? "border-gold bg-gold/[0.07]" : "border-white/12 hover:border-white/30"
                                        }`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="font-display text-xl uppercase tracking-tight leading-tight">{p.name}</div>
                                            {sel.package_key === p.key && <BadgeCheck className="w-4 h-4 text-gold shrink-0" />}
                                        </div>
                                        <div className="mt-2 font-display text-2xl text-gold">{formatINR(p.price)}</div>
                                        <div className="text-xs text-zinc-400 mt-1">{p.duration_label}</div>
                                        <div className="mono text-[9px] tracking-[0.16em] text-zinc-500 mt-1">{p.validity_label}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="mono text-[10px] tracking-[0.22em] text-zinc-500">YOUR CONSULTANT</div>
                            <div className="mt-3 grid sm:grid-cols-2 gap-3">
                                {doctors.map((d) => (
                                    <button key={d.id} onClick={() => { setSel((s) => ({ ...s, doctor_id: d.id })); setSlot(null); }}
                                        data-testid={`booking-doctor-${d.id}`}
                                        className={`text-left p-4 border flex items-center gap-3 transition-colors ${
                                            sel.doctor_id === d.id ? "border-gold bg-gold/[0.07]" : "border-white/12 hover:border-white/30"
                                        }`}>
                                        <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                                            {d.photo_url ? <img src={d.photo_url} alt="" className="w-full h-full object-cover" />
                                                : <Stethoscope className="w-4 h-4 text-zinc-400" />}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-sm text-white truncate">{d.name}</div>
                                            <div className="text-[11px] text-zinc-500 truncate">{d.qualification || d.title}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="mono text-[10px] tracking-[0.22em] text-zinc-500">WHAT DO YOU WANT HELP WITH?</div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {areas.map((a) => (
                                    <button key={a.key} onClick={() => setSel((s) => ({ ...s, area_key: s.area_key === a.key ? "" : a.key }))}
                                        data-testid={`booking-area-${a.key}`}
                                        className={`px-4 py-2 border text-xs transition-colors ${
                                            sel.area_key === a.key ? "border-gold text-gold bg-gold/[0.07]" : "border-white/12 text-zinc-300 hover:border-white/30"
                                        }`}>
                                        {a.name}
                                    </button>
                                ))}
                            </div>
                            <p className="mt-3 text-xs text-zinc-500">Optional — it helps your consultant prepare the right questions.</p>
                        </div>
                    </div>
                )}

                {/* STEP 2 */}
                {step === 2 && (
                    <div className="mt-10" data-testid="booking-step-2">
                        <div className="p-5 border border-white/10 bg-bg1 flex items-start gap-3">
                            <CalendarClock className="w-4 h-4 text-gold mt-0.5" />
                            <div className="text-sm text-zinc-300">
                                Live availability for <span className="text-white">{doctor?.name}</span>. Only free slots are shown —
                                the moment you confirm, the slot is held for you.
                            </div>
                        </div>
                        <div className="mt-8">
                            <SlotPicker doctorId={sel.doctor_id} value={slot} onChange={setSlot} testPrefix="booking-slot" />
                        </div>
                        {slot && (
                            <div className="mt-6 mono text-[11px] tracking-[0.16em] text-lime" data-testid="booking-slot-selected">
                                SELECTED: {slot.slot_date} · {slot.label}
                            </div>
                        )}
                    </div>
                )}

                {/* STEP 3 */}
                {step === 3 && (
                    <div className="mt-10 space-y-5" data-testid="booking-step-3">
                        {!customer && (
                            <div className="p-4 border border-gold/30 bg-gold/[0.06] text-xs text-zinc-300 leading-relaxed">
                                No account needed — we create your patient account automatically so you can complete intake,
                                upload reports and message your consultant. Already registered?{" "}
                                <a href="/account/login" className="text-gold underline" data-testid="booking-signin-link">Sign in</a>.
                            </div>
                        )}
                        <div className="grid sm:grid-cols-2 gap-4">
                            <label><Label>PATIENT NAME *</Label>
                                <Input value={form.name} onChange={(e) => set({ name: e.target.value })}
                                    data-testid="booking-name" placeholder="Full name" /></label>
                            <label><Label>PHONE (WHATSAPP) *</Label>
                                <Input value={form.phone} onChange={(e) => set({ phone: e.target.value })}
                                    data-testid="booking-phone" placeholder="10-digit mobile" /></label>
                            <label className="sm:col-span-2"><Label>EMAIL *</Label>
                                <Input value={form.email} onChange={(e) => set({ email: e.target.value })}
                                    data-testid="booking-email" placeholder="you@email.com" /></label>
                            <label><Label>AGE</Label>
                                <Input value={form.age} onChange={(e) => set({ age: e.target.value })} data-testid="booking-age" /></label>
                            <label><Label>GENDER</Label>
                                <select value={form.gender} onChange={(e) => set({ gender: e.target.value })}
                                    data-testid="booking-gender"
                                    className="w-full bg-bg0 border border-white/12 text-white px-3 py-3 text-sm outline-none focus:border-gold">
                                    <option value="">Prefer not to say</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </select></label>
                            <label><Label>CITY</Label>
                                <Input value={form.city} onChange={(e) => set({ city: e.target.value })} data-testid="booking-city" /></label>
                            <label><Label>CONSULTATION IS FOR</Label>
                                <select value={form.for_whom} onChange={(e) => set({ for_whom: e.target.value })}
                                    data-testid="booking-for-whom"
                                    className="w-full bg-bg0 border border-white/12 text-white px-3 py-3 text-sm outline-none focus:border-gold">
                                    <option value="self">Myself</option>
                                    <option value="family">A family member</option>
                                </select></label>
                            {form.for_whom === "family" && (
                                <label className="sm:col-span-2"><Label>FAMILY MEMBER NAME</Label>
                                    <Input value={form.member_name} onChange={(e) => set({ member_name: e.target.value })}
                                        data-testid="booking-member-name" /></label>
                            )}
                            <label className="sm:col-span-2"><Label>ANYTHING WE SHOULD KNOW BEFORE THE CALL?</Label>
                                <textarea value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={3}
                                    data-testid="booking-notes"
                                    className="w-full bg-bg0 border border-white/12 text-white px-3 py-3 text-sm outline-none focus:border-gold placeholder:text-zinc-600"
                                    placeholder="Optional" /></label>
                        </div>
                    </div>
                )}

                {/* STEP 4 */}
                {step === 4 && (
                    <div className="mt-10" data-testid="booking-step-4">
                        <div className="border border-white/10 bg-bg1 p-6">
                            <div className="mono text-[10px] tracking-[0.22em] text-zinc-500">SUMMARY</div>
                            <div className="mt-4 space-y-2.5 text-sm">
                                <Row k="Plan" v={pkg?.name} />
                                <Row k="Consultant" v={doctor?.name} />
                                {sel.area_key && <Row k="Area" v={areas.find((a) => a.key === sel.area_key)?.name} />}
                                <Row k="Appointment" v={`${slot?.slot_date} · ${slot?.label} IST`} />
                                <Row k="Patient" v={form.for_whom === "family" ? `${form.member_name || form.name} (family)` : form.name} />
                                <Row k="Contact" v={`${form.phone} · ${form.email}`} />
                            </div>
                            <div className="mt-5 pt-5 border-t border-white/10 flex items-end justify-between">
                                <div className="mono text-[10px] tracking-[0.22em] text-zinc-500">AMOUNT PAYABLE</div>
                                {freeConsult ? (
                                    <div className="text-right" data-testid="booking-amount">
                                        <div className="font-display text-4xl text-lime">FREE</div>
                                        <div className="mono text-[9px] tracking-[0.16em] text-zinc-500 mt-1 line-through">
                                            {formatINR(pkg?.price || 0)}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="font-display text-4xl text-gold" data-testid="booking-amount">{formatINR(pkg?.price || 0)}</div>
                                )}
                            </div>
                            {freeConsult && (
                                <div className="mono text-[10px] tracking-[0.16em] text-lime mt-3" data-testid="booking-free-note">
                                    YOUR FIRST CONSULTATION IS ON US — NOTHING TO PAY
                                </div>
                            )}
                        </div>

                        <div className="mt-5 p-5 border border-gold/30 bg-gold/[0.06] flex items-start gap-3">
                            <CreditCard className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                            <div className="text-xs text-zinc-300 leading-relaxed">
                                {freeConsult ? (
                                    <>Nothing to pay for this appointment. Your slot is held for you — you can complete
                                    the patient intake form right after this step.</>
                                ) : (
                                    <>You will be taken to <span className="text-white">Cashfree's secure checkout</span> —
                                    UPI, cards, net banking and wallets. Your slot is held while you pay, and the
                                    appointment is confirmed the moment the payment clears.</>
                                )}
                            </div>
                        </div>

                        <button onClick={confirm} disabled={saving} data-testid="booking-confirm-button"
                            className="mt-7 w-full btn-pill btn-primary justify-center">
                            {saving ? <ButtonRing label={freeConsult ? "CONFIRMING…" : "OPENING PAYMENT…"} />
                                : freeConsult
                                    ? <>Confirm Appointment <ArrowRight className="w-4 h-4 ml-2 inline" /></>
                                    : <>Pay {formatINR(pkg?.price || 0)} Securely <ArrowRight className="w-4 h-4 ml-2 inline" /></>}
                        </button>
                        <div className="mt-4 flex items-center gap-2 justify-center text-[11px] text-zinc-500">
                            <ShieldCheck className="w-3.5 h-3.5 text-lime" /> Your health information stays private and is visible only to your consultant.
                        </div>
                    </div>
                )}

                {/* NAV */}
                <div className="mt-10 flex items-center justify-between">
                    <button onClick={() => (step === 1 ? nav("/consultation") : setStep(step - 1))}
                        data-testid="booking-back-button" className="btn-pill btn-secondary">
                        <ArrowLeft className="w-3.5 h-3.5 mr-2 inline" /> {step === 1 ? "Back" : "Previous"}
                    </button>
                    {step < 4 && (
                        <button onClick={next} data-testid="booking-next-button" className="btn-pill btn-primary">
                            Continue <ArrowRight className="w-3.5 h-3.5 ml-2 inline" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

const Row = ({ k, v }) => (
    <div className="flex items-start justify-between gap-6">
        <span className="text-zinc-500">{k}</span>
        <span className="text-white text-right">{v || "—"}</span>
    </div>
);
